import PropTypes from 'prop-types';
import { useHistory, useRouteMatch } from 'react-router';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import {
  Button,
  Col,
  Collapse,
  Drawer,
  Layout,
  Menu,
  Popover,
  Row,
  Space,
} from 'antd';

import { flatRoutes, getSelectedMenuKeys, menuFormatter } from '@/utils/route';

import './style.less';
import { useDisclosure, useWindowSize } from '@/hooks';
import { useBreadcrumbs } from '@/contexts/BreadcrumbContextProvider';
import { ChevronRight, X } from 'react-feather';
import { CrownOutlined } from '@ant-design/icons';
import { hasComboPermissions } from '@/utils/functions/hasComboPermissions';
import { useCallback, useEffect, useState } from 'react';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import moment from 'moment';
import { Group } from '@/components/_common';
import ActionIcon from '@/components/_common/ActionIcon';
import { getAuthCodeAndRedirect } from '@/utils/functions/getAuthCodeAndRedirect';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { useMatrix } from '@/core/domain/enterprise/Matrix/Matrix.hooks';
import { isAppPortocar } from '@/index';

const AppMenu = ({ routes }) => {
  const match = useRouteMatch();
  const history = useHistory();
  const { breadcrumbs } = useBreadcrumbs();
  const { isMobile } = useWindowSize();
  const [showDrawer, setShowDrawer] = useDisclosure();

  const menuArr = routes;
  const menuData = menuFormatter(menuArr);
  const flatMenuData = flatRoutes(menuData);

  const menuKeys = flatMenuData.map((route) =>
    route.path === '/app' ? '/app/home' : route.path,
  );
  const selectedMenuKeys = getSelectedMenuKeys(
    menuKeys,
    history.location.pathname,
  );

  const hasPermissionGP = hasPermissions([Authorities.YARDMANAGEMENT]);

  const [hasSpedEnabled, setHasSpedEnabled] = useState(false);
  const { data: matrix } = useMatrix();

  useEffect(() => {
    if (matrix) {
      setHasSpedEnabled(matrix.matrixSettings.isRegisteredInPlugStorage);
    }
  }, [matrix]);

  const renderMenuLink = (item) => {
    const { target, name } = item;
    let { path } = item;

    const hasCombo =
      !item?.permissions || hasComboPermissions(item?.permissions, true);

    Object.keys(match.params).forEach((key) => {
      path = path.replace(`:${key}`, match.params[key]);
    });

    return (
      <Link
        to={path}
        target={target}
        replace={path === history.location.pathname}
      >
        <Space>
          {!hasCombo && <CrownOutlined style={{ color: '#1FA2FF' }} />}

          <span
            style={
              !hasCombo
                ? {
                    background:
                      '-webkit-linear-gradient(45deg, #1FA2FF, #12D8FA, #1FA2FF)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold',
                  }
                : {}
            }
          >
            <FormattedMessage id={name} />
          </span>
        </Space>
      </Link>
    );
  };

  const renderSubMenuOrItem = useCallback(
    (appRoute) => {
      const { name, path } = appRoute;

      if (path.includes('/app/fiscal/sped') && !hasSpedEnabled) return null;

      if (appRoute.routes && appRoute.routes.some((child) => child.name)) {
        const childrenItems = renderMenuItems(appRoute.routes);

        if (childrenItems) {
          return (
            <Menu.SubMenu
              key={path}
              title={
                <Space style={{ display: 'flex' }}>
                  <FormattedMessage id={name} />
                </Space>
              }
              popupClassName="submenu-popup"
            >
              {childrenItems}
            </Menu.SubMenu>
          );
        }

        return null;
      }

      return (
        <Menu.Item key={path === '/app' ? '/app/home' : path}>
          {renderMenuLink(appRoute)}
        </Menu.Item>
      );
    },
    [hasSpedEnabled],
  );

  const filterScheduleMenuInPortocar = (menu) =>
    !isAppPortocar || menu.name !== 'layout.menu.schedules';

  const renderMenuItems = (data) => {
    if (!data) return [];

    return data
      .filter((item) => item.name)
      .filter(filterScheduleMenuInPortocar)
      .map((item) => renderSubMenuOrItem(item))
      .filter((item) => item);
  };

  const pushAndClose = ({ path }) => {
    history.push(path);
    setShowDrawer.close();
  };

  const listRoutes = useCallback(() => {
    return menuData.filter(filterScheduleMenuInPortocar).map((cur) => {
      if (cur.routes && cur.routes.length >= 1) {
        return (
          <Collapse>
            <Collapse.Panel header={<FormattedMessage id={cur.name} />}>
              <Space direction="vertical" size={0}>
                {cur.routes.map((curRoute) => {
                  if (
                    curRoute.path.includes('/app/fiscal/sped') &&
                    !hasSpedEnabled
                  ) {
                    return null;
                  }

                  if (curRoute.routes && curRoute.routes.length >= 1) {
                    return (
                      <Collapse ghost>
                        <Collapse.Panel
                          header={<FormattedMessage id={curRoute.name} />}
                        >
                          <Space direction="vertical" size={0}>
                            {curRoute.routes.map((subCur) => (
                              <Button
                                type="link"
                                onClick={() => pushAndClose(subCur)}
                              >
                                {<FormattedMessage id={subCur.name} />}
                              </Button>
                            ))}
                          </Space>
                        </Collapse.Panel>
                      </Collapse>
                    );
                  } else {
                    return (
                      <Button
                        type="link"
                        onClick={() => pushAndClose(curRoute)}
                      >
                        {<FormattedMessage id={curRoute.name} />}
                      </Button>
                    );
                  }
                })}
              </Space>
            </Collapse.Panel>
          </Collapse>
        );
      } else {
        return (
          <Button type="link" onClick={() => pushAndClose(cur)}>
            {<FormattedMessage id={cur.name} />}
          </Button>
        );
      }
    });
  }, [hasSpedEnabled]);

  const [codeLoading, setCodeLoading] = useState(false);

  const redirectToGP = async () => {
    setCodeLoading(true);

    try {
      const destinationUrl = process.env.REACT_APP_GP_URL;

      await getAuthCodeAndRedirect(destinationUrl, '_blank', {
        destinationUrl: destinationUrl + '/auth-code',
      });
    } catch {
    } finally {
      setCodeLoading(false);
    }
  };

  const [hasVisualized, setHasVisualized] = useState(() => {
    const visualized = localStorage.getItem(
      '@ultracar-gestao-patio-visualized',
    );

    return visualized ? JSON.parse(visualized) : false;
  });

  const workorderYeardTooltip = useCallback(
    (component) => {
      return (
        <Popover
          open={
            !hasVisualized &&
            moment().isBefore(moment('2024-08-01'), 'milliseconds')
          }
          placement="bottomLeft"
          content={
            !hasVisualized ? (
              <div style={{ width: 300 }} onClick={(e) => e.stopPropagation()}>
                <Group position="between" style={{ marginBottom: 4 }}>
                  <strong>Novidade! Gestão de pátio</strong>

                  <ActionIcon
                    onClick={() => {
                      setHasVisualized(true);
                      localStorage.setItem(
                        '@ultracar-gestao-patio-visualized',
                        'true',
                      );
                    }}
                  >
                    <X size={16} />
                  </ActionIcon>
                </Group>
                <div>
                  Gerencie o pátio e os agendamentos com uma plataforma
                  customizavel para o dia-a-dia da sua oficina.
                </div>
              </div>
            ) : undefined
          }
        >
          <div
            onClick={() => {
              setHasVisualized(true);
              localStorage.setItem('@ultracar-gestao-patio-visualized', 'true');
            }}
          >
            {component}
          </div>
        </Popover>
      );
    },
    [hasVisualized],
  );

  return (
    <>
      {codeLoading && (
        <ModalLoadingOverlay
          messages={['Redirecionando para o Gestão de Pátio']}
        />
      )}

      {!isMobile ? (
        <Row>
          <Col span={24}>
            <Layout.Header style={{ height: '64px' }} className="menu-wrapper">
              <Menu
                mode="horizontal"
                className="menu"
                // defaultOpenKeys={selectedMenuKeys}
                selectedKeys={selectedMenuKeys.length ? selectedMenuKeys : []}
              >
                {hasPermissionGP && (
                  <Menu.Item
                    key="workshop-yeard"
                    onClick={redirectToGP}
                    className="menu-gp"
                  >
                    {workorderYeardTooltip(
                      <span className="menu-gp-label">Gestão de Pátio</span>,
                    )}
                  </Menu.Item>
                )}

                {renderMenuItems(menuData)}
              </Menu>
            </Layout.Header>
          </Col>
        </Row>
      ) : (
        <Row align="middle">
          {hasPermissionGP && (
            <Col>
              {workorderYeardTooltip(
                <div
                  className="menu-gp"
                  style={{ marginLeft: 4 }}
                  onClick={redirectToGP}
                >
                  <span className="menu-gp-label">Gestão de Pátio</span>
                </div>,
              )}
            </Col>
          )}

          <Col flex={1} className="menu-wrapper">
            <Button type="link" size="large" onClick={setShowDrawer.open}>
              <Space>
                Menu
                <ChevronRight />
                {!!breadcrumbs[0] && (
                  <FormattedMessage
                    id={breadcrumbs[breadcrumbs.length - 1].label}
                  />
                )}
              </Space>
            </Button>
          </Col>

          <Drawer
            title={
              !!breadcrumbs[0] && (
                <FormattedMessage
                  id={breadcrumbs[breadcrumbs.length - 1].label}
                />
              )
            }
            open={showDrawer}
            placement="left"
            onClose={setShowDrawer.close}
          >
            <Space direction="vertical" size={0} style={{ width: '100%' }}>
              {listRoutes()}
            </Space>
          </Drawer>
        </Row>
      )}
    </>
  );
};

AppMenu.propTypes = {
  routes: PropTypes.array.isRequired,
};

export default AppMenu;
